import { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, matchPath, useLocation, useNavigate } from 'react-router';
import moment from 'moment';
import { publishProject as publishProjectAction } from '../project-actions';
import ConfirmModal from '../../components/confirm-modal/ConfirmModal';
import { trackEvent } from '../../support-and-tracking/analytics-actions';
import {
    canPublishProject,
    getProjectType,
    hasErrorsInUserForProjectPublish,
    isProjectPartOfVM2025AndDateBeforeStart,
} from '../project-model';
import ButtonText from '../../components/button/button-text/ButtonText';
import Chevron from '../../components/chevron/Chevron';
import { Subtitle } from '../../components/typography/Heading';
import List from '../../components/text/List';
import VisibilitySensor from '../../components/visibility-sensor/VisibilitySensor';
import RoundedButtonAction from '../../components/button/rounded-button-action/RoundedButtonAction';
import styles from './publish-button.module.less';
import { getInvitationFromProjectId } from '../project-invitation-reducer';
import { useUser } from '../../user/useUser';
import { capitalize, formatToNbDate } from '../../utils';
import { useFeature } from '../../components/hooks/isFeatureEnabled';
import {
    VERIFY_COLLECTOR_IDENTITY,
    VERIFY_COLLECTOR_TO_ORG,
    VERIFY_COLLECTOR_WITH_ACCOUNT_NUMBER,
} from '../../constants/features/constants';
import { trackProjectPublish } from '../../support-and-tracking/tracking-functions';
import { getCustomRewardsForProject } from '../projects-reducer';
import { PUBLSHING_PROJECT_STOPPED } from '../../constants';
import { useProjectWizardABCTest } from '../../components/hooks/projectWizardAbcTest';

const EDIT_PATH = '/project/:projectId/edit';
const PublishButton = ({
    className = '',
    disabled = false,
    project = null,
    showTips = false,
    school,
    extraText = null,
    hideWhenNotValid = false,
    alwaysEnabled = false,
    showExtraFirst,
    ...props
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const user = useUser();
    const projectInvitation = useSelector((state) => getInvitationFromProjectId(state, project?.id));
    const isPublishing = useSelector((state) => state.projects.isPublishing);
    const [showTip, setShowTip] = useState(false);
    const myRef = useRef(null);
    const confirmModalRef = useRef(null);
    const rewards = useSelector((state) => getCustomRewardsForProject(state, project?.id));
    const showTermsForRewardAddresses =
        project?.template_id && rewards && rewards.some((r) => r.requires_address || r.requires_phonenumber);
    const executeScroll = () => {
        window ? window.scrollTo(0, myRef.current.offsetTop) : () => {};
    };
    const showVerifyUser = useFeature(VERIFY_COLLECTOR_IDENTITY);
    const verifyUserAccountNumber = useFeature(VERIFY_COLLECTOR_WITH_ACCOUNT_NUMBER);
    const verifyCollectorsToOrg = useFeature(VERIFY_COLLECTOR_TO_ORG);
    const projectWizardABCTest = useProjectWizardABCTest();

    const userErrors = hasErrorsInUserForProjectPublish({
        user,
        project,
        verifyUserIdentity: showVerifyUser,
        verifyUserAccountNumber,
        verifyCollectorsToOrg,
    });

    const publishProject = () => {
        const publishedAt = project.profile?.publishedAtOverride;
        dispatch(publishProjectAction({ projectId: project.id, publishedAt }))
            .then(() => {
                trackProjectPublish({ project, projectWizardABCTest });
                navigate(`/project/${project.id}`, { replace: true });
            })
            .finally(() => {
                confirmModalRef?.current?.close();
            });
    };

    const startPublish = () => {
        if (
            !canPublishProject({
                user,
                project,
                invitation: projectInvitation,
                school,
            })
        ) {
            dispatch({ type: PUBLSHING_PROJECT_STOPPED });
            return;
        }
        if (hasUserErrors && matchPath(EDIT_PATH, location.pathname)) {
            dispatch({ type: PUBLSHING_PROJECT_STOPPED });
            navigate(`/project/${project.id}/start`, { replace: true });
            return;
        }
        if (isProjectPartOfVM2025AndDateBeforeStart(project)) {
            publishProject();
            return;
        }
        confirmModalRef?.current?.open();
    };

    const getButtonText = () => {
        if (isProjectPartOfVM2025AndDateBeforeStart(project)) {
            return isPublishing ? 'Starter …' : 'Klargjør dugnaden  ';
        }
        return isPublishing ? 'Starter …' : `Start ${projectType}en`;
    };

    const getConfirmModalMessage = () => {
        if (isProjectPartOfVM2025AndDateBeforeStart(project)) {
            return `Dere kan starte salget ${formatToNbDate(project.profile?.publishedAtOverride)}`;
        }
        if (project.profile?.publishedAtOverride && moment(project.profile?.publishedAtOverride).isAfter(moment())) {
            return `${capitalize(projectType)}en vil være åpen for innbetalinger fra ${moment(
                project.profile?.publishedAtOverride
            ).format('LLLL')}`;
        }
        return `Er du sikker på at du vil starte ${projectType}en?`;
    };

    const projectType = getProjectType(project);
    const buttonText = getButtonText(project);

    if (!project) {
        return (
            <RoundedButtonAction disabled className={[styles.publishButton, className].join(' ')} {...props}>
                {buttonText}
            </RoundedButtonAction>
        );
    }

    const hasUserErrors = Object.keys(userErrors).length > 0;

    const canPublish =
        canPublishProject({
            user,
            project,
            invitation: projectInvitation,
            school,
        }) && !hasUserErrors;

    if (hideWhenNotValid && ((!canPublish && !alwaysEnabled) || showTermsForRewardAddresses)) {
        return null;
    }
    return (
        <>
            {showExtraFirst && extraText && <div className={styles.extraText}>{extraText}</div>}
            <RoundedButtonAction
                disabled={(!canPublish && !alwaysEnabled) || disabled}
                onClick={startPublish}
                trackingCategory="publish-project"
                trackingAction="start"
                className={[styles.publishButton, className].join(' ')}
                {...props}
            >
                {buttonText}
            </RoundedButtonAction>
            {!showExtraFirst && extraText && <div className={styles.extraText}>{extraText}</div>}
            {showTips && (
                <ButtonText
                    onClick={() => {
                        trackEvent('tip', '10-12-before-publish', { projectId: project.id, openTip: !showTip });
                        setShowTip(!showTip);
                    }}
                    className={styles.tipButton}
                >
                    Tips for å lykkes med spleisen ✨
                    <Chevron isOpen={showTip} className={styles.chevron} />
                </ButtonText>
            )}
            {!!showTip && (
                <div className={styles.tipContainer}>
                    <Subtitle tag="h2" className={styles.tipMainTitle}>
                        Engasjer folk tidlig 💡
                    </Subtitle>
                    <p>Skaff 10 givere de første 12 timene! Da øker du sannsynligheten for å lykkes 🙌</p>
                    <List className={styles.tipList}>
                        <li>Når en spleis først har fått litt fart er folk mer villige til å støtte</li>
                        <li>Engasjer venner, familie og nettverket ditt, og få de til å dele spleisen videre</li>
                        <li>Del spleisen på sosiale medier, e-post, SMS og alle andre steder du kan nå folk</li>
                    </List>
                    <p className={styles.tipLink}>
                        <Link to="/tips" target="_blank" ref={myRef}>
                            Se flere gode tips
                        </Link>
                    </p>
                    <VisibilitySensor onVisible={executeScroll} offset={{ bottom: 1000 }} />
                </div>
            )}

            <ConfirmModal
                modalRef={confirmModalRef}
                title={`Alt er klart for at ${projectType}en kan starte`}
                message={getConfirmModalMessage()}
                successText={buttonText}
                cancelText="Avbryt"
                onClickSuccess={publishProject}
                onClickCancel={() => confirmModalRef?.current?.close()}
                useActionButton={true}
            />
        </>
    );
};

export default PublishButton;
